import { csvCell } from '~/utils/csvCell';

export type Row = Record<string, string | number | null>;
export type Filters = {
  from: string;
  to: string;
  attribution: string;
  country: string;
  channel: string;
  detail: string;
  segment: string;
  router: string;
  variant: string;
  view: string;
  metric: string;
  split: string;
  layout: string;
  theme: string;
  sort: string;
  cadence: string;
  split2: string;
  timeSplit: string;
  highlight: string;
  sortBy: string;
  campaignGroup: string;
  filtersOpen: string;
  measures: string;
};
export const DEFAULT_ATTR = 'Last Click 30D Non-Passive (excludes D,I,OS,PSB)';
export const defaults: Filters = {
  from: '',
  to: '',
  attribution: DEFAULT_ATTR,
  country: 'All',
  channel: 'All',
  detail: 'All',
  segment: 'All',
  router: 'All',
  variant: 'All',
  view: 'Performance',
  metric: 'GM (BR)',
  split: 'campaign_segment',
  layout: 'standard',
  theme: 'light',
  sort: 'desc',
  cadence: 'Daily',
  split2: 'All',
  timeSplit: 'Full Period',
  highlight: '',
  sortBy: 'Costs',
  campaignGroup: 'All',
  filtersOpen: 'false',
  measures: 'core',
};
export const dimensions: Record<string, string> = {
  All: 'All',
  campaign_segment: 'Campaign Segment',
  traffic_channel: 'Traffic Channel',
  traffic_channel_detail: 'Traffic Channel Detail',
  country: 'Country',
  campaign_segment_perf_marketing: 'Campaign segment (PerfMkt)',
  marketing_router_id: 'Router',
  marketing_router_variant_id: 'Variant',
};
export const metrics: Record<
  string,
  { numerator: string; denominator?: string; subtract?: string; format: string; definition: string }
> = {
  'Margin ROAS (BR)': {
    numerator: 'gm',
    denominator: 'cost',
    format: 'ratio',
    definition: 'SUM(gm) / SUM(cost), before refunds. This is margin ROAS, not revenue ROAS.',
  },
  'Margin ROAS (AR)': {
    numerator: 'gm_ar',
    denominator: 'cost',
    format: 'ratio',
    definition: 'SUM(gm_ar) / SUM(cost), after refunds.',
  },
  'TTV ROAS (BR)': {
    numerator: 'ttv',
    denominator: 'cost',
    format: 'ratio',
    definition: 'SUM(ttv) / SUM(cost). TTV is gross sales in AUD, not net revenue.',
  },
  'TTV ROAS (AR)': {
    numerator: 'ttv_ar',
    denominator: 'cost',
    format: 'ratio',
    definition: 'SUM(ttv_ar) / SUM(cost), after refunds.',
  },
  Costs: {
    numerator: 'cost',
    format: 'money',
    definition: 'SUM(cost), media costs in AUD. Router variants receive a session-proportional allocation.',
  },
  'GM (BR)': { numerator: 'gm', format: 'money', definition: 'SUM(gm), gross margin in AUD before refunds.' },
  'GM (AR)': { numerator: 'gm_ar', format: 'money', definition: 'SUM(gm_ar), gross margin in AUD after refunds.' },
  'TTV (BR)': { numerator: 'ttv', format: 'money', definition: 'SUM(ttv), gross sales in AUD before refunds.' },
  'TTV (AR)': { numerator: 'ttv_ar', format: 'money', definition: 'SUM(ttv_ar), gross sales in AUD after refunds.' },
  'Discounts (BR)': {
    numerator: 'customer_discount',
    format: 'money',
    definition: 'Promo plus voucher sales on non-cancelled items, in AUD.',
  },
  'Discounts (AR)': {
    numerator: 'customer_discount_ar',
    format: 'money',
    definition: 'Customer discount less refunded promo and voucher portions on non-cancelled items.',
  },
  'GM after discounts (BR)': {
    numerator: 'gm',
    subtract: 'customer_discount',
    format: 'money',
    definition: 'SUM(gm) - SUM(customer_discount).',
  },
  'GM after discounts (AR)': {
    numerator: 'gm_ar',
    subtract: 'customer_discount_ar',
    format: 'money',
    definition: 'SUM(gm_ar) - SUM(customer_discount_ar).',
  },
  'ROAS after discounts (BR)': {
    numerator: 'gm',
    subtract: 'customer_discount',
    denominator: 'cost',
    format: 'ratio',
    definition: '(SUM(gm) - SUM(customer_discount)) / SUM(cost).',
  },
  'ROAS after discounts (AR)': {
    numerator: 'gm_ar',
    subtract: 'customer_discount_ar',
    denominator: 'cost',
    format: 'ratio',
    definition: '(SUM(gm_ar) - SUM(customer_discount_ar)) / SUM(cost).',
  },
  Clicks: { numerator: 'clicks', format: 'number', definition: 'SUM(clicks). Allocated across router variants.' },
  Impressions: {
    numerator: 'impressions',
    format: 'number',
    definition: 'SUM(impressions). Allocated across router variants.',
  },
  Sessions: {
    numerator: 'sessions',
    format: 'number',
    definition: 'SUM(sessions). Shapley has no session measurement.',
  },
  Orders: {
    numerator: 'orders',
    format: 'number',
    definition: 'SUM(orders), attributed orders. Shapley can assign fractional credit.',
  },
  'Sign-ups': {
    numerator: 'sign_ups',
    format: 'number',
    definition: 'SUM(sign_ups), website sign-ups. These are not acquired purchasing customers.',
  },
  'Cost per sign-up': {
    numerator: 'cost',
    denominator: 'sign_ups',
    format: 'money',
    definition: 'SUM(cost) / SUM(sign_ups). Original workbook returns null when sign-ups are zero. This is not CAC.',
  },
  CPC: { numerator: 'cost', denominator: 'clicks', format: 'money', definition: 'SUM(cost) / SUM(clicks).' },
  CTR: {
    numerator: 'clicks',
    denominator: 'impressions',
    format: 'percent',
    definition: 'SUM(clicks) / SUM(impressions).',
  },
  'Conversion rate': {
    numerator: 'orders',
    denominator: 'sessions',
    format: 'percent',
    definition: 'SUM(orders) / SUM(sessions).',
  },
  CAC: {
    numerator: '__unknown__',
    format: 'money',
    definition:
      'Unknown. No verified acquired-customer denominator or CAC calculation in the inspected workbook. Cost per sign-up must not be relabelled CAC.',
  },
};
// One categorical bar series: labels identify groups; mint singles out Paid Search.
export const channelColours: Record<string, string> = { 'Paid Search': '#4EFEB9' };
export function chartColours(view: string, split: string, attribution: string): Record<string, string> {
  return view === 'Attribution' ? { [attribution]: '#4EFEB9' } : split === 'traffic_channel' ? channelColours : {};
}
const filterKeys: Record<string, string> = {
  country: 'country',
  channel: 'traffic_channel',
  detail: 'traffic_channel_detail',
  segment: 'campaign_segment_perf_marketing',
  campaignGroup: 'campaign_segment',
  router: 'marketing_router_id',
  variant: 'marketing_router_variant_id',
};
export const label = (v: unknown) => (v == null ? 'Unknown' : String(v));
export function readFilters(params: URLSearchParams): Filters {
  const f = { ...defaults };
  for (const k of Object.keys(f) as Array<keyof Filters>) {
    f[k] = params.get(k) ?? f[k];
  }
  if (!metrics[f.metric]) {
    f.metric = defaults.metric;
  }
  if (!dimensions[f.split]) {
    f.split = defaults.split;
  }
  if (!dimensions[f.split2]) {
    f.split2 = 'All';
  }
  if (!['Daily', 'Weekly', 'Monthly', 'Annually'].includes(f.cadence)) {
    f.cadence = 'Daily';
  }
  if (!['Full Period', 'Date Cadence'].includes(f.timeSplit)) {
    f.timeSplit = 'Full Period';
  }
  if (!['Performance', 'Attribution', 'Product mix', 'PoP', 'Sales', 'Monthly'].includes(f.view)) {
    f.view = defaults.view;
  }
  if (!['standard', 'baseline'].includes(f.layout)) {
    f.layout = 'standard';
  }
  if (!['light', 'dark'].includes(f.theme)) {
    f.theme = 'light';
  }
  return f;
}
export function validate(f: Filters) {
  const valid = (v: string) =>
    /^\d{4}-\d{2}-\d{2}$/.test(v) &&
    Number.isFinite(Date.parse(v + 'T00:00:00Z')) &&
    new Date(v + 'T00:00:00Z').toISOString().slice(0, 10) === v;
  if (!valid(f.from) || !valid(f.to) || f.from > f.to) {
    return 'Choose a valid date range in chronological order.';
  }

  return '';
}
export function selectRows(rows: Array<Row>, f: Filters, compare = false) {
  if (validate(f)) {
    return [];
  }
  return rows.filter(
    (r) =>
      String(r.p_date) >= f.from &&
      String(r.p_date) <= f.to &&
      (compare || r.attribution_model === f.attribution) &&
      Object.entries(filterKeys).every(
        ([key, col]) => f[key as keyof Filters] === 'All' || label(r[col]) === f[key as keyof Filters],
      ),
  );
}
export function sum(rows: Array<Row>, key: string): number | null {
  const a = rows.map((r) => r[key]).filter((v): v is number => typeof v === 'number' && Number.isFinite(v));
  return a.length ? a.reduce((x, y) => x + y, 0) : null;
}
export function divide(a: number | null, b: number | null) {
  return a === null || b === null || b === 0 ? null : a / b;
}
export function value(rows: Array<Row>, metric: string): number | null {
  const m = metrics[metric];
  if (!m) {
    return null;
  }
  let a = sum(rows, m.numerator);
  if (m.subtract) {
    const b = sum(rows, m.subtract);
    a = a === null || b === null ? null : a - b;
  }
  return m.denominator ? divide(a, sum(rows, m.denominator)) : a;
}
export function group(rows: Array<Row>, dimension: string, metric: string) {
  const buckets = new Map<string, Array<Row>>();
  for (const r of rows) {
    const k = label(r[dimension]);
    if (!buckets.has(k)) {
      buckets.set(k, []);
    }
    buckets.get(k).push(r);
  }
  return [...buckets].map(([name, r]) => ({
    name,
    value: value(r, metric),
    cost: sum(r, 'cost'),
    gm: sum(r, 'gm'),
    gm_ar: sum(r, 'gm_ar'),
    orders: sum(r, 'orders'),
    roas: value(r, 'Margin ROAS (BR)'),
    rows: r,
  }));
}
export function format(v: number | null, metric: string, compact = false): string {
  if (v === null || !Number.isFinite(v)) {
    return 'Unknown';
  }
  const m = metrics[metric]?.format ?? metric;
  return (
    (m === 'money' ? 'A$' : '') +
    (m === 'percent'
      ? (v * 100).toLocaleString('en-AU', { maximumFractionDigits: 2 })
      : v.toLocaleString('en-AU', {
          maximumFractionDigits: m === 'ratio' ? 2 : 2,
          ...(compact && Math.abs(v) >= 10000 ? { notation: 'compact' as const } : {}),
        })) +
    (m === 'percent' ? '%' : m === 'ratio' ? '×' : '')
  );
}
export function toCSV(data: ReturnType<typeof group>, f: Filters, context: Record<string, string> = {}) {
  const escape = (value: unknown) => csvCell(value, 'Unknown');
  return [
    [
      'Group',
      f.metric,
      'Costs (A$)',
      'GM BR (A$)',
      'GM AR (A$)',
      'Orders',
      'From',
      'To',
      'Attribution',
      'Country',
      'Channel',
      'Source',
    ],
    ...data.map((r) => [
      r.name,
      r.value,
      r.cost,
      r.gm,
      r.gm_ar,
      r.orders,
      f.from,
      f.to,
      f.view === 'Attribution' ? r.name : f.attribution,
      f.country,
      f.channel,
      'lux-analytics-prod.analytics.perf_mkt_reporting_roas',
    ]),
  ]
    .map((r, index) =>
      [...r, ...Object.values(index ? context : Object.fromEntries(Object.keys(context).map((key) => [key, key])))]
        .map(escape)
        .join(','),
    )
    .join('\r\n');
}

export function productRows(rows: Array<Row>) {
  return ['ltle', 'lpp', 'lpc'].map((k) => ({
    name: k.toUpperCase(),
    gm: sum(rows, k + '_gm'),
    orders: sum(rows, k + '_orders'),
    share: divide(sum(rows, k + '_orders'), sum(rows, 'orders')),
  }));
}
export function toProductCSV(
  products: ReturnType<typeof productRows>,
  f: Filters,
  context: Record<string, string> = {},
) {
  const escape = (value: unknown) => csvCell(value, 'Unknown');
  return [
    [
      'Product',
      'GM BR (A$)',
      'Attributed orders',
      '% of all orders',
      'From',
      'To',
      'Attribution',
      'Country',
      'Channel',
      'Channel detail',
      'Campaign segment (PerfMkt)',
      'Router',
      'Variant',
      'Source',
      'Definition',
    ],
    ...products.map((p) => [
      p.name,
      p.gm,
      p.orders,
      p.share === null ? null : p.share * 100,
      f.from,
      f.to,
      f.attribution,
      f.country,
      f.channel,
      f.detail,
      f.segment,
      f.router,
      f.variant,
      'lux-analytics-prod.analytics.perf_mkt_reporting_roas',
      'Overlapping product orders; shares must not be added to 100%',
    ]),
  ]
    .map((r, index) =>
      [...r, ...Object.values(index ? context : Object.fromEntries(Object.keys(context).map((key) => [key, key])))]
        .map(escape)
        .join(','),
    )
    .join('\r\n');
}

// Original worksheet parameter labels and metric aliases are retained for familiarity.
export const metricAliases: Record<string, string> = {
  'ROAS (Margin) (BR)': 'Margin ROAS (BR)',
  'ROAS (Margin) (AR)': 'Margin ROAS (AR)',
  'ROAS (TTV) (BR)': 'TTV ROAS (BR)',
  'ROAS (TTV) (AR)': 'TTV ROAS (AR)',
  'Sign-Ups': 'Sign-ups',
  'Cost per Sign-Up': 'Cost per sign-up',
  'CR (Orders/Sessions)': 'Conversion rate',
  'Customer Discounts (BR)': 'Discounts (BR)',
  'Customer Discounts (AR)': 'Discounts (AR)',
  'GM (BR) After Customer Discount': 'GM after discounts (BR)',
  'GM (AR) After Customer Discount': 'GM after discounts (AR)',
  'ROAS (GM (BR)) After Customer Discount': 'ROAS after discounts (BR)',
  'ROAS (GM (AR)) After Customer Discount': 'ROAS after discounts (AR)',
};
Object.entries(metricAliases).forEach(([alias, key]) => {
  metrics[alias] = metrics[key];
});
Object.assign(metrics, {
  'Sessions / Clicks': {
    numerator: 'sessions',
    denominator: 'clicks',
    format: 'percent',
    definition: 'SUM(sessions) / SUM(clicks).',
  },
  'Bounce Rate': {
    numerator: 'bounces',
    denominator: 'sessions',
    format: 'percent',
    definition: 'SUM(bounces) / SUM(sessions).',
  },
  'LuxPlus+ Sign-Ups': {
    numerator: 'luxplus_new_signups',
    format: 'number',
    definition: 'SUM(luxplus_new_signups), attributed orders with a new membership.',
  },
  'New Buyer GM (BR) / GM (BR)': {
    numerator: 'first_sale_gm',
    denominator: 'gm',
    format: 'percent',
    definition: 'SUM(first_sale_gm) / SUM(gm).',
  },
  'ROAS (New Buyer Margin) (BR)': {
    numerator: 'first_sale_gm',
    denominator: 'cost',
    format: 'ratio',
    definition: 'SUM(first_sale_gm) / SUM(cost).',
  },
  'ROAS (Margin) (BR) (Cash only)': {
    numerator: 'cash_only_gm',
    denominator: 'cost',
    format: 'ratio',
    definition: 'SUM(cash_only_gm) / SUM(cost).',
  },
});
for (const [code, name] of [
  ['ltle', 'LTLE'],
  ['lpp', 'LPP'],
  ['lpc', 'LPC'],
  ['tour', 'Tours'],
  ['cruise', 'Cruises'],
  ['experience', 'Exp'],
  ['luxplus', 'LP+'],
]) {
  metrics[`% of GM from ${name} (BR)`] = {
    numerator: code + '_gm',
    denominator: 'gm',
    format: 'percent',
    definition: `SUM(${code}_gm) / SUM(gm), before refunds.`,
  };
}
export const metricOptions = [
  'Costs',
  'Clicks',
  'CPC',
  'CTR',
  'Sessions',
  'Sessions / Clicks',
  'GM (BR)',
  'GM (AR)',
  'TTV (BR)',
  'TTV (AR)',
  'ROAS (Margin) (BR)',
  'ROAS (Margin) (AR)',
  'ROAS (TTV) (BR)',
  'ROAS (TTV) (AR)',
  'Sign-Ups',
  'Cost per Sign-Up',
  'Orders',
  'CR (Orders/Sessions)',
  'Customer Discounts (BR)',
  'Customer Discounts (AR)',
  'GM (BR) After Customer Discount',
  'GM (AR) After Customer Discount',
  'ROAS (GM (BR)) After Customer Discount',
  'ROAS (GM (AR)) After Customer Discount',
];
export const tableMetrics = [
  'Costs',
  '% of Costs',
  'Impressions',
  'Clicks',
  'CPC',
  'CTR',
  'Sessions',
  'Sessions / Clicks',
  'Bounce Rate',
  'Orders',
  'CR (Orders/Sessions)',
  'GM (BR)',
  'GM (AR)',
  'LuxPlus+ Sign-Ups',
  'New Buyer GM (BR) / GM (BR)',
  'ROAS (Margin) (BR)',
  'ROAS (New Buyer Margin) (BR)',
  'ROAS (Margin) (AR)',
  'TTV (BR)',
  'ROAS (TTV) (BR)',
  'ROAS (TTV) (AR)',
  'ROAS (Margin) (BR) (Cash only)',
  '% of Orders from LTLE',
  '% of Orders from LPP',
  '% of Orders from LPC',
  '% of GM from LTLE (BR)',
  '% of GM from LPP (BR)',
  '% of GM from LPC (BR)',
  '% of GM from Tours (BR)',
  '% of GM from Cruises (BR)',
  '% of GM from Exp (BR)',
  '% of GM from LP+ (BR)',
  'GM (BR) After Customer Discount',
  'GM (AR) After Customer Discount',
  'Customer Discounts (BR)',
  'Customer Discounts (AR)',
  'Sign-Ups',
  'ROAS (GM (BR)) After Customer Discount',
  'ROAS (GM (AR)) After Customer Discount',
];
export const cadenceOptions = ['Daily', 'Weekly', 'Monthly', 'Annually'];
export function bucketDate(date: string, cadence: string) {
  const d = new Date(date + 'T00:00:00Z');
  if (cadence === 'Weekly') {
    d.setUTCDate(d.getUTCDate() - ((d.getUTCDay() + 6) % 7));
  }
  if (cadence === 'Monthly') {
    d.setUTCDate(1);
  }
  if (cadence === 'Annually') {
    d.setUTCMonth(0);
    d.setUTCDate(1);
  }
  return d.toISOString().slice(0, 10);
}
export function dimensionLabel(row: Row, key: string) {
  return key === 'All' ? 'All' : label(row[key]);
}
export function buildSeries(rows: Array<Row>, f: Filters, metric = f.metric) {
  const buckets = new Map<string, Map<string, Array<Row>>>();
  const names = new Set<string>();
  for (const row of rows) {
    const date = bucketDate(String(row.p_date), f.cadence);
    const name = dimensionLabel(row, f.split);
    names.add(name);
    if (!buckets.has(date)) {
      buckets.set(date, new Map());
    }
    const b = buckets.get(date);
    if (!b.has(name)) {
      b.set(name, []);
    }
    b.get(name).push(row);
  }
  const series = [...names].sort();
  const points = [...buckets]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, b]) => ({
      date,
      values: Object.fromEntries(series.map((name) => [name, value(b.get(name) ?? [], metric)])),
    }));
  // Revision 15.4 thumbnail confirms a normalised stacked area. PctTotal / Columns is applied within each date. Interactive partition sign-off remains pending.
  const relative = points.map((p) => {
    const total = series
      .map((name) => p.values[name])
      .reduce<number | null>((a, v) => (v === null ? a : (a ?? 0) + v), null);
    return { date: p.date, values: Object.fromEntries(series.map((name) => [name, divide(p.values[name], total)])) };
  });
  return { series, points, relative };
}
export function buildTable(rows: Array<Row>, f: Filters) {
  const buckets = new Map<string, { time: string; first: string; second: string; rows: Array<Row> }>();
  for (const row of rows) {
    const time = f.timeSplit === 'Date Cadence' ? bucketDate(String(row.p_date), f.cadence) : 'Full Period';
    const first = dimensionLabel(row, f.split);
    const second = dimensionLabel(row, f.split2);
    const k = JSON.stringify([time, first, second]);
    if (!buckets.has(k)) {
      buckets.set(k, { time, first, second, rows: [] });
    }
    buckets.get(k).rows.push(row);
  }
  const out = [...buckets].map(([id, b]) => ({
    id,
    time: b.time,
    first: b.first,
    second: b.second,
    values: Object.fromEntries(
      tableMetrics.map((m) => [
        m,
        m === '% of Costs' ? divide(sum(b.rows, 'cost'), sum(rows, 'cost')) : value(b.rows, m),
      ]),
    ),
    costShare: divide(sum(b.rows, 'cost'), sum(rows, 'cost')),
  }));
  return out.sort((a, b) => {
    const k = f.sortBy;
    const av = k === 'Group' ? a.first : k === 'Date' ? a.time : (a.values[k] ?? -Infinity);
    const bv = k === 'Group' ? b.first : k === 'Date' ? b.time : (b.values[k] ?? -Infinity);
    const cmp = typeof av === 'number' && typeof bv === 'number' ? av - bv : String(av).localeCompare(String(bv));
    return cmp * (f.sort === 'asc' ? 1 : -1);
  });
}
export function toTableCSV(table: ReturnType<typeof buildTable>, f: Filters, context: Record<string, string> = {}) {
  const esc = (value: unknown) => csvCell(value, 'Unknown');
  return [
    [
      'Time Split',
      'Data Split 1',
      'Data Split 2',
      ...tableMetrics,
      'From',
      'To',
      'Attribution',
      'Country',
      'Channel',
      'Source',
    ],
    ...table.map((r) => [
      r.time,
      r.first,
      r.second,
      ...tableMetrics.map((m) => r.values[m]),
      f.from,
      f.to,
      f.attribution,
      f.country,
      f.channel,
      'lux-analytics-prod.analytics.perf_mkt_reporting_roas',
    ]),
  ]
    .map((r, index) => [...r, ...(index ? Object.values(context) : Object.keys(context))].map(esc).join(','))
    .join('\r\n');
}
export const LE_SERIES = ['#0F464F', '#76BFAA', '#247A79', '#56B5A0', '#183E59', '#8FDCC0', '#39767E', '#B4EBDC'];
export function seriesColour(name: string, index: number, theme: string) {
  if (index === 0) {
    return theme === 'dark' ? '#9ACCC8' : '#0F464F';
  }
  return LE_SERIES[index % LE_SERIES.length];
}

metrics['% of Costs'] = {
  numerator: 'cost',
  denominator: 'cost',
  format: 'percent',
  definition: 'Group costs divided by total costs in the selected scope. The table shows 100% for the grand total.',
};
for (const k of ['ltle', 'lpp', 'lpc']) {
  metrics['% of Orders from ' + k.toUpperCase()] = {
    numerator: k + '_orders',
    denominator: 'orders',
    format: 'percent',
    definition: 'SUM(' + k + '_orders) / SUM(orders). Product orders overlap; shares are not mutually exclusive.',
  };
}

export const coreTableMetrics = ['Costs', 'GM (BR)', 'GM (AR)', 'ROAS (Margin) (BR)', 'Orders', 'CR (Orders/Sessions)'];
export function attributionGroupOrder(groups: Map<string, Array<Row>>, attribution: string) {
  const rows = [...groups].map(([key, scope]) => ({
    key,
    groups: JSON.parse(key) as Array<string>,
    cost: sum(scope, 'cost'),
  }));
  const channelCosts = new Map(rows.filter((r) => r.groups[1] === attribution).map((r) => [r.groups[0], r.cost]));
  return rows.sort(
    (a, b) =>
      (channelCosts.get(b.groups[0]) ?? -Infinity) - (channelCosts.get(a.groups[0]) ?? -Infinity) ||
      a.groups[0].localeCompare(b.groups[0]) ||
      a.groups[1].localeCompare(b.groups[1]),
  );
}
export function toAttributionCSV(rows: Array<Row>, f: Filters, context: Record<string, string> = {}) {
  const channels = [...new Set(rows.map((r) => label(r.traffic_channel)))].sort();
  const models = [...new Set(rows.map((r) => label(r.attribution_model)))].sort();
  const countries = [...new Set(rows.map((r) => label(r.country)))].sort();
  const byChannelModel = new Map<string, Array<Row>>(),
    byCountry = new Map<string, Array<Row>>(),
    byModel = new Map<string, Array<Row>>();
  for (const row of rows) {
    const channel = label(row.traffic_channel),
      model = label(row.attribution_model),
      country = label(row.country);
    for (const [map, key] of [
      [byChannelModel, JSON.stringify([channel, model])],
      [byCountry, JSON.stringify([channel, model, country])],
      [byModel, model],
    ] as const) {
      if (!map.has(key)) {
        map.set(key, []);
      }
      map.get(key).push(row);
    }
  }
  const records: Array<Array<unknown>> = [];
  for (const [sheet, metric] of [
    ['ROAS (GM) (BR)', 'ROAS (Margin) (BR)'],
    ['CR (Orders / Sessions)', 'CR (Orders/Sessions)'],
  ]) {
    for (const model of models) {
      records.push([sheet, 'Grand Total', model, 'All', metric, value(byModel.get(model) ?? [], metric)]);
    }
    for (const channel of channels) {
      for (const model of models) {
        records.push([
          sheet,
          channel,
          model,
          'All',
          metric,
          value(byChannelModel.get(JSON.stringify([channel, model])) ?? [], metric),
        ]);
      }
    }
  }
  const detail = attributionGroupOrder(byChannelModel, f.attribution);
  for (const { groups } of detail) {
    for (const country of countries) {
      for (const metric of ['ROAS (Margin) (BR)', 'ROAS (TTV) (BR)']) {
        records.push([
          'ROAS (sorted by costs)',
          ...groups,
          country,
          metric,
          value(byCountry.get(JSON.stringify([...groups, country])) ?? [], metric),
        ]);
      }
    }
  }
  const esc = (value: unknown) => csvCell(value, 'Unknown');
  return [
    ['Worksheet', 'Traffic Channel', 'Attribution model', 'Country', 'Metric', 'Value', 'From', 'To', 'Source'],
    ...records.map((r) => [...r, f.from, f.to, 'lux-analytics-prod.analytics.perf_mkt_reporting_roas']),
  ]
    .map((r, index) => [...r, ...(index ? Object.values(context) : Object.keys(context))].map(esc).join(','))
    .join('\r\n');
}
